/**
 * Safe expression language for the user-script custom-widget builder.
 *
 * The widget builder lets a non-developer author the *value* a custom
 * widget shows by writing a short expression against the widget's data
 * source — e.g. `round(oee.availability * oee.performance * oee.quality, 1)`
 * or `count > 0 ? upper(status) : "idle"`.
 *
 * Critically this is NOT `eval` / `new Function`. Running operator-authored
 * strings through the JS engine would be a remote-code-execution hole (the
 * scripts can be persisted server-side and replayed in other operators'
 * browsers). Instead this module ships a tiny, fully-sandboxed expression
 * language with a hand-written tokenizer + recursive-descent parser + AST
 * walker. The grammar is intentionally small and total:
 *
 *   expr        := ternary
 *   ternary     := or ( "?" expr ":" expr )?
 *   or          := and ( "||" and )*
 *   and         := equality ( "&&" equality )*
 *   equality    := comparison ( ("==" | "!=") comparison )*
 *   comparison  := additive ( ("<" | "<=" | ">" | ">=") additive )*
 *   additive    := multiplicative ( ("+" | "-") multiplicative )*
 *   multiplicative := unary ( ("*" | "/" | "%") unary )*
 *   unary       := ("-" | "!") unary | primary
 *   primary     := number | string | "true" | "false" | "null"
 *                | call | path | "(" expr ")"
 *   call        := IDENT "(" ( expr ( "," expr )* )? ")"
 *   path        := IDENT ( "." IDENT )*
 *
 * Only a whitelisted set of pure functions is callable; there is no member
 * invocation, no property assignment, no loops, and no access to globals.
 * The worst a malicious script can do is produce a wrong number or throw a
 * controlled `ScriptError` — never reach `window`, `fetch`, or the DOM.
 *
 * This file is pure logic (no React) so it is unit-tested directly with
 * `node --test`; the React builder surface (`custom-widget-builder.tsx`)
 * imports `validateScript` for inline editor feedback and `evaluateScript`
 * for the (sample-data) preview.
 *
 * ── Relationship to the backend script sandbox ──────────────────────────
 * This grammar is a deliberate SUPERSET of the backend
 * `WidgetScriptSandboxService` `SimpleExpression` language (arithmetic +
 * dotted-identifier access). The backend has no ternary / logical /
 * comparison / string / function support, so scripts that use those richer
 * constructs are PREVIEW-ONLY in this builder and cannot round-trip to the
 * server sandbox. `classifyBackendCompatibility` below is the shared contract
 * that tells the builder whether a given script falls inside the backend
 * `SimpleExpression` subset (so it can be persisted + executed server-side via
 * `POST /api/v1/widgets/scripts` with `language: "SimpleExpression"`) or is
 * client-preview-only. There is intentionally no client-side JS-eval path —
 * the backend's sandboxed JavaScript runtime is a separate, gated seam.
 */

// ────────────────────────────────────────────────────────────────────────
// Errors
// ────────────────────────────────────────────────────────────────────────

/** Thrown for any tokenize / parse / evaluate failure. Carries a 0-based
 *  source position when known so the editor can underline the offending
 *  token. */
export class ScriptError extends Error {
  /** 0-based character offset into the source, or -1 when not positional. */
  readonly position: number;
  constructor(message: string, position = -1) {
    super(message);
    this.name = 'ScriptError';
    this.position = position;
  }
}

// ────────────────────────────────────────────────────────────────────────
// Tokenizer
// ────────────────────────────────────────────────────────────────────────

type TokenType =
  | 'number'
  | 'string'
  | 'ident'
  | 'op'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'dot'
  | 'eof';

interface Token {
  type: TokenType;
  value: string;
  /** 0-based start offset in the source. */
  pos: number;
}

const TWO_CHAR_OPS = new Set(['==', '!=', '<=', '>=', '&&', '||']);
const ONE_CHAR_OPS = new Set(['+', '-', '*', '/', '%', '<', '>', '!', '?', ':']);

function isIdentStart(ch: string): boolean {
  return /[A-Za-z_]/.test(ch);
}
function isIdentPart(ch: string): boolean {
  return /[A-Za-z0-9_]/.test(ch);
}
function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}

export function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = src.length;

  while (i < n) {
    const ch = src[i];
    if (ch === undefined) break;

    // Whitespace.
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }

    // String literal — single or double quoted, with \" / \' / \\ escapes.
    if (ch === '"' || ch === "'") {
      const quote = ch;
      const start = i;
      i++;
      let value = '';
      let closed = false;
      while (i < n) {
        const c = src[i];
        if (c === '\\' && i + 1 < n) {
          const next = src[i + 1];
          value += next === 'n' ? '\n' : next === 't' ? '\t' : next;
          i += 2;
          continue;
        }
        if (c === quote) {
          closed = true;
          i++;
          break;
        }
        value += c;
        i++;
      }
      if (!closed) {
        throw new ScriptError('Unterminated string literal.', start);
      }
      tokens.push({ type: 'string', value, pos: start });
      continue;
    }

    // Number literal (integer or decimal).
    if (isDigit(ch) || (ch === '.' && i + 1 < n && isDigit(src[i + 1] ?? ''))) {
      const start = i;
      let seenDot = false;
      while (i < n && (isDigit(src[i] ?? '') || (src[i] === '.' && !seenDot))) {
        if (src[i] === '.') seenDot = true;
        i++;
      }
      tokens.push({ type: 'number', value: src.slice(start, i), pos: start });
      continue;
    }

    // Identifier / keyword.
    if (isIdentStart(ch)) {
      const start = i;
      while (i < n && isIdentPart(src[i] ?? '')) i++;
      tokens.push({ type: 'ident', value: src.slice(start, i), pos: start });
      continue;
    }

    // Punctuation + operators.
    if (ch === '(') {
      tokens.push({ type: 'lparen', value: ch, pos: i });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen', value: ch, pos: i });
      i++;
      continue;
    }
    if (ch === ',') {
      tokens.push({ type: 'comma', value: ch, pos: i });
      i++;
      continue;
    }
    if (ch === '.') {
      tokens.push({ type: 'dot', value: ch, pos: i });
      i++;
      continue;
    }

    const two = src.slice(i, i + 2);
    if (TWO_CHAR_OPS.has(two)) {
      tokens.push({ type: 'op', value: two, pos: i });
      i += 2;
      continue;
    }
    if (ONE_CHAR_OPS.has(ch)) {
      tokens.push({ type: 'op', value: ch, pos: i });
      i++;
      continue;
    }

    throw new ScriptError(`Unexpected character '${ch}'.`, i);
  }

  tokens.push({ type: 'eof', value: '', pos: n });
  return tokens;
}

// ────────────────────────────────────────────────────────────────────────
// AST
// ────────────────────────────────────────────────────────────────────────

export type Ast =
  | { kind: 'literal'; value: number | string | boolean | null }
  | { kind: 'path'; segments: string[]; pos: number }
  | { kind: 'unary'; op: '-' | '!'; operand: Ast }
  | { kind: 'binary'; op: string; left: Ast; right: Ast; pos: number }
  | { kind: 'ternary'; cond: Ast; then: Ast; else: Ast }
  | { kind: 'call'; name: string; args: Ast[]; pos: number };

// ────────────────────────────────────────────────────────────────────────
// Whitelisted functions — name → arity range. The evaluator holds the
// implementations; this table is the parse-time validity check.
// ────────────────────────────────────────────────────────────────────────

interface FnSpec {
  min: number;
  max: number;
}

export const BUILTIN_FUNCTIONS: Readonly<Record<string, FnSpec>> = {
  round: { min: 1, max: 2 },
  abs: { min: 1, max: 1 },
  min: { min: 1, max: Infinity },
  max: { min: 1, max: Infinity },
  ceil: { min: 1, max: 1 },
  floor: { min: 1, max: 1 },
  upper: { min: 1, max: 1 },
  lower: { min: 1, max: 1 },
  len: { min: 1, max: 1 },
  coalesce: { min: 1, max: Infinity },
  concat: { min: 1, max: Infinity },
  number: { min: 1, max: 1 },
  string: { min: 1, max: 1 },
};

const RESERVED = new Set(['true', 'false', 'null']);

// ────────────────────────────────────────────────────────────────────────
// Parser — recursive descent over the token stream.
// ────────────────────────────────────────────────────────────────────────

class Parser {
  private pos = 0;
  private readonly tokens: Token[];
  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  private peek(): Token {
    const t = this.tokens[this.pos];
    if (t === undefined) {
      throw new ScriptError('Unexpected end of token stream.', 0);
    }
    return t;
  }
  private next(): Token {
    const t = this.tokens[this.pos++];
    if (t === undefined) {
      throw new ScriptError('Unexpected end of token stream.', 0);
    }
    return t;
  }
  private isOp(value: string): boolean {
    const t = this.peek();
    return t.type === 'op' && t.value === value;
  }
  private eatOp(value: string): boolean {
    if (this.isOp(value)) {
      this.pos++;
      return true;
    }
    return false;
  }

  parse(): Ast {
    const expr = this.parseTernary();
    const t = this.peek();
    if (t.type !== 'eof') {
      throw new ScriptError(`Unexpected token '${t.value}'.`, t.pos);
    }
    return expr;
  }

  private parseTernary(): Ast {
    const cond = this.parseOr();
    if (this.eatOp('?')) {
      const then = this.parseTernary();
      if (!this.eatOp(':')) {
        const t = this.peek();
        throw new ScriptError("Expected ':' in conditional expression.", t.pos);
      }
      const els = this.parseTernary();
      return { kind: 'ternary', cond, then, else: els };
    }
    return cond;
  }

  private parseBinaryLevel(ops: string[], next: () => Ast): Ast {
    let left = next();
    for (;;) {
      const t = this.peek();
      if (t.type === 'op' && ops.includes(t.value)) {
        this.pos++;
        const right = next();
        left = { kind: 'binary', op: t.value, left, right, pos: t.pos };
      } else {
        return left;
      }
    }
  }

  private parseOr(): Ast {
    return this.parseBinaryLevel(['||'], () => this.parseAnd());
  }
  private parseAnd(): Ast {
    return this.parseBinaryLevel(['&&'], () => this.parseEquality());
  }
  private parseEquality(): Ast {
    return this.parseBinaryLevel(['==', '!='], () => this.parseComparison());
  }
  private parseComparison(): Ast {
    return this.parseBinaryLevel(['<', '<=', '>', '>='], () => this.parseAdditive());
  }
  private parseAdditive(): Ast {
    return this.parseBinaryLevel(['+', '-'], () => this.parseMultiplicative());
  }
  private parseMultiplicative(): Ast {
    return this.parseBinaryLevel(['*', '/', '%'], () => this.parseUnary());
  }

  private parseUnary(): Ast {
    if (this.isOp('-') || this.isOp('!')) {
      const op = this.next().value as '-' | '!';
      return { kind: 'unary', op, operand: this.parseUnary() };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): Ast {
    const t = this.peek();

    if (t.type === 'number') {
      this.pos++;
      return { kind: 'literal', value: Number(t.value) };
    }
    if (t.type === 'string') {
      this.pos++;
      return { kind: 'literal', value: t.value };
    }
    if (t.type === 'lparen') {
      this.pos++;
      const inner = this.parseTernary();
      const close = this.peek();
      if (close.type !== 'rparen') {
        throw new ScriptError("Expected ')'.", close.pos);
      }
      this.pos++;
      return inner;
    }
    if (t.type === 'ident') {
      // Reserved keyword literals.
      if (t.value === 'true' || t.value === 'false') {
        this.pos++;
        return { kind: 'literal', value: t.value === 'true' };
      }
      if (t.value === 'null') {
        this.pos++;
        return { kind: 'literal', value: null };
      }
      // Function call: IDENT "(" ...
      if (this.tokens[this.pos + 1]?.type === 'lparen') {
        return this.parseCall();
      }
      // Otherwise a dotted path.
      return this.parsePath();
    }

    throw new ScriptError(
      t.type === 'eof' ? 'Unexpected end of expression.' : `Unexpected token '${t.value}'.`,
      t.pos,
    );
  }

  private parseCall(): Ast {
    const nameTok = this.next(); // ident
    const name = nameTok.value;
    this.next(); // lparen
    const args: Ast[] = [];
    if (this.peek().type !== 'rparen') {
      for (;;) {
        args.push(this.parseTernary());
        if (this.peek().type === 'comma') {
          this.pos++;
          continue;
        }
        break;
      }
    }
    const close = this.peek();
    if (close.type !== 'rparen') {
      throw new ScriptError(`Expected ')' to close call to '${name}'.`, close.pos);
    }
    this.pos++;
    return { kind: 'call', name, args, pos: nameTok.pos };
  }

  private parsePath(): Ast {
    const first = this.next(); // ident
    const segments = [first.value];
    while (this.peek().type === 'dot') {
      this.pos++;
      const seg = this.peek();
      if (seg.type !== 'ident') {
        throw new ScriptError('Expected a field name after ".".', seg.pos);
      }
      this.pos++;
      segments.push(seg.value);
    }
    return { kind: 'path', segments, pos: first.pos };
  }
}

/** Parse a script into an AST. Throws `ScriptError` on the first problem. */
export function parseScript(src: string): Ast {
  return new Parser(tokenize(src)).parse();
}

// ────────────────────────────────────────────────────────────────────────
// Static validation — parse + walk the AST for semantic problems that the
// grammar alone doesn't catch (unknown functions, wrong arity, referenced
// fields outside the declared allow-list).
// ────────────────────────────────────────────────────────────────────────

export interface ValidateOptions {
  /**
   * Optional allow-list of top-level field names the script may reference
   * (the keys exposed by the widget's data source). When provided, a path
   * whose first segment is not in this set is reported. When omitted, any
   * field reference is allowed (the editor can't know the shape yet).
   */
  allowedFields?: readonly string[];
}

export interface ValidationIssue {
  message: string;
  /** 0-based source offset, or -1 when not positional. */
  position: number;
}

export interface ValidationResult {
  ok: boolean;
  /** Parsed AST when `ok` is true, else null. */
  ast: Ast | null;
  issues: ValidationIssue[];
  /** Distinct top-level field names referenced by the script. */
  referencedFields: string[];
}

/**
 * Validate a script without evaluating it. Returns every issue found (the
 * parser stops at the first syntax error, but semantic checks accumulate),
 * plus the set of referenced fields so the builder can warn about typos.
 */
export function validateScript(src: string, opts: ValidateOptions = {}): ValidationResult {
  const trimmed = src.trim();
  if (trimmed.length === 0) {
    return {
      ok: false,
      ast: null,
      issues: [{ message: 'Script is empty.', position: 0 }],
      referencedFields: [],
    };
  }

  let ast: Ast;
  try {
    ast = parseScript(src);
  } catch (err) {
    const e = err as ScriptError;
    return {
      ok: false,
      ast: null,
      issues: [{ message: e.message, position: e.position ?? -1 }],
      referencedFields: [],
    };
  }

  const issues: ValidationIssue[] = [];
  const fields = new Set<string>();
  const allowed = opts.allowedFields ? new Set(opts.allowedFields) : null;

  const walk = (node: Ast): void => {
    switch (node.kind) {
      case 'literal':
        return;
      case 'path': {
        const head = node.segments[0];
        if (head === undefined) return;
        fields.add(head);
        if (RESERVED.has(head)) {
          issues.push({ message: `'${head}' is a reserved word.`, position: node.pos });
        } else if (allowed && !allowed.has(head)) {
          issues.push({
            message: `Unknown field '${head}'. Available: ${[...allowed].join(', ') || '(none)'}.`,
            position: node.pos,
          });
        }
        return;
      }
      case 'unary':
        walk(node.operand);
        return;
      case 'binary':
        walk(node.left);
        walk(node.right);
        return;
      case 'ternary':
        walk(node.cond);
        walk(node.then);
        walk(node.else);
        return;
      case 'call': {
        const spec = BUILTIN_FUNCTIONS[node.name];
        if (!spec) {
          issues.push({
            message: `Unknown function '${node.name}'. Allowed: ${Object.keys(BUILTIN_FUNCTIONS).join(', ')}.`,
            position: node.pos,
          });
        } else if (node.args.length < spec.min || node.args.length > spec.max) {
          const range =
            spec.max === Infinity
              ? `at least ${spec.min}`
              : spec.min === spec.max
                ? `exactly ${spec.min}`
                : `${spec.min}–${spec.max}`;
          issues.push({
            message: `'${node.name}' expects ${range} argument(s), got ${node.args.length}.`,
            position: node.pos,
          });
        }
        node.args.forEach(walk);
        return;
      }
    }
  };

  walk(ast);

  return {
    ok: issues.length === 0,
    ast: issues.length === 0 ? ast : null,
    issues,
    referencedFields: [...fields],
  };
}

// ────────────────────────────────────────────────────────────────────────
// Backend-compatibility classification — the shared contract that reconciles
// this front-end grammar with the backend `SimpleExpression` sandbox.
// ────────────────────────────────────────────────────────────────────────

/** Backend `WidgetScriptLanguage.SimpleExpression`, as the API enum spells it. */
export const BACKEND_SIMPLE_EXPRESSION = 'SimpleExpression' as const;

export interface BackendCompatibility {
  /**
   * True when the script uses only constructs the backend
   * `SimpleExpression` sandbox supports (number literals, dotted-identifier
   * paths, unary minus, and the `+ - * /` binary operators). Such a script can
   * be persisted + executed server-side. False scripts are client preview-only.
   */
  backendCompatible: boolean;
  /**
   * The backend script language to persist with when compatible, else null.
   */
  backendLanguage: typeof BACKEND_SIMPLE_EXPRESSION | null;
  /** Human-readable reasons a script is preview-only (empty when compatible). */
  reasons: string[];
}

/**
 * Classify a parsed/validated script against the backend `SimpleExpression`
 * subset. The builder uses this to decide whether `onSave` may persist the
 * script to the server sandbox (`POST /api/v1/widgets/scripts`) or whether it
 * is a client-only preview widget. Unlike `validateScript` this does NOT judge
 * correctness — a script can be perfectly valid here yet preview-only because
 * it uses richer grammar than the backend understands.
 */
export function classifyBackendCompatibility(src: string): BackendCompatibility {
  let ast: Ast;
  try {
    ast = parseScript(src);
  } catch {
    // Unparseable scripts are neither compatible nor previewable; let
    // validateScript surface the syntax error. Report incompatible here.
    return {
      backendCompatible: false,
      backendLanguage: null,
      reasons: ['Script does not parse.'],
    };
  }

  const reasons = new Set<string>();

  const walk = (node: Ast): void => {
    switch (node.kind) {
      case 'literal':
        if (typeof node.value !== 'number') {
          reasons.add('String / boolean / null literals are not supported by the backend SimpleExpression sandbox.');
        }
        return;
      case 'path':
        return; // dotted identifier access is supported
      case 'unary':
        if (node.op !== '-') {
          reasons.add(`Unary '${node.op}' is not supported by the backend sandbox.`);
        }
        walk(node.operand);
        return;
      case 'binary':
        if (!['+', '-', '*', '/'].includes(node.op)) {
          reasons.add(`Operator '${node.op}' is not supported by the backend SimpleExpression sandbox.`);
        }
        walk(node.left);
        walk(node.right);
        return;
      case 'ternary':
        reasons.add('Conditional (ternary) expressions are not supported by the backend sandbox.');
        walk(node.cond);
        walk(node.then);
        walk(node.else);
        return;
      case 'call':
        reasons.add(`Function calls (e.g. '${node.name}') are not supported by the backend sandbox.`);
        node.args.forEach(walk);
        return;
    }
  };

  walk(ast);

  const backendCompatible = reasons.size === 0;
  return {
    backendCompatible,
    backendLanguage: backendCompatible ? BACKEND_SIMPLE_EXPRESSION : null,
    reasons: [...reasons],
  };
}

// ────────────────────────────────────────────────────────────────────────
// Evaluation — walk the AST against a data record. Used by the preview.
// ────────────────────────────────────────────────────────────────────────

export type ScriptValue = number | string | boolean | null;

function toNumber(v: ScriptValue): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v === null) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function toBool(v: ScriptValue): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return v.length > 0;
  return false;
}

// Segments that would let an author-written path escape the data record onto
// the prototype chain. Traversing these resolves to null (the not-found path).
const FORBIDDEN_PATH_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor']);

function lookupPath(data: Record<string, unknown>, segments: string[]): ScriptValue {
  let cur: unknown = data;
  for (const seg of segments) {
    if (cur == null || typeof cur !== 'object') return null;
    // Only traverse OWN data fields; forbidden/prototype keys resolve to null
    // instead of walking the prototype chain.
    if (FORBIDDEN_PATH_SEGMENTS.has(seg) || !Object.prototype.hasOwnProperty.call(cur, seg)) {
      return null;
    }
    cur = (cur as Record<string, unknown>)[seg];
  }
  if (cur == null) return null;
  if (typeof cur === 'number' || typeof cur === 'string' || typeof cur === 'boolean') {
    return cur;
  }
  // Objects / arrays collapse to null — the language only deals in scalars.
  return null;
}

function callBuiltin(name: string, args: ScriptValue[]): ScriptValue {
  switch (name) {
    case 'round': {
      const digits = args.length > 1 ? Math.trunc(toNumber(args[1] ?? null)) : 0;
      const factor = 10 ** digits;
      return Math.round(toNumber(args[0] ?? null) * factor) / factor;
    }
    case 'abs':
      return Math.abs(toNumber(args[0] ?? null));
    case 'ceil':
      return Math.ceil(toNumber(args[0] ?? null));
    case 'floor':
      return Math.floor(toNumber(args[0] ?? null));
    case 'min':
      return Math.min(...args.map(toNumber));
    case 'max':
      return Math.max(...args.map(toNumber));
    case 'upper':
      return String(args[0] ?? '').toUpperCase();
    case 'lower':
      return String(args[0] ?? '').toLowerCase();
    case 'len':
      return String(args[0] ?? '').length;
    case 'coalesce': {
      for (const a of args) if (a !== null && a !== undefined) return a;
      return null;
    }
    case 'concat':
      return args.map((a) => (a === null ? '' : String(a))).join('');
    case 'number':
      return toNumber(args[0] ?? null);
    case 'string':
      return args[0] === null ? '' : String(args[0]);
    default:
      throw new ScriptError(`Unknown function '${name}'.`);
  }
}

function evalNode(node: Ast, data: Record<string, unknown>): ScriptValue {
  switch (node.kind) {
    case 'literal':
      return node.value;
    case 'path':
      return lookupPath(data, node.segments);
    case 'unary': {
      const v = evalNode(node.operand, data);
      return node.op === '-' ? -toNumber(v) : !toBool(v);
    }
    case 'ternary':
      return toBool(evalNode(node.cond, data))
        ? evalNode(node.then, data)
        : evalNode(node.else, data);
    case 'call':
      return callBuiltin(
        node.name,
        node.args.map((a) => evalNode(a, data)),
      );
    case 'binary': {
      const op = node.op;
      // Short-circuit logical operators.
      if (op === '&&') {
        const l = evalNode(node.left, data);
        return toBool(l) ? evalNode(node.right, data) : l;
      }
      if (op === '||') {
        const l = evalNode(node.left, data);
        return toBool(l) ? l : evalNode(node.right, data);
      }
      const l = evalNode(node.left, data);
      const r = evalNode(node.right, data);
      switch (op) {
        case '+':
          // String concatenation when either side is a string.
          if (typeof l === 'string' || typeof r === 'string') {
            return `${l ?? ''}${r ?? ''}`;
          }
          return toNumber(l) + toNumber(r);
        case '-':
          return toNumber(l) - toNumber(r);
        case '*':
          return toNumber(l) * toNumber(r);
        case '/': {
          const d = toNumber(r);
          if (d === 0) throw new ScriptError('Division by zero.');
          return toNumber(l) / d;
        }
        case '%': {
          const d = toNumber(r);
          if (d === 0) throw new ScriptError('Modulo by zero.');
          return toNumber(l) % d;
        }
        case '==':
          return l === r;
        case '!=':
          return l !== r;
        case '<':
          return toNumber(l) < toNumber(r);
        case '<=':
          return toNumber(l) <= toNumber(r);
        case '>':
          return toNumber(l) > toNumber(r);
        case '>=':
          return toNumber(l) >= toNumber(r);
        default:
          throw new ScriptError(`Unsupported operator '${op}'.`);
      }
    }
  }
}

export interface EvaluateResult {
  ok: boolean;
  value: ScriptValue;
  error: string | null;
}

/**
 * Evaluate a script against a sample data record. Never throws — failures
 * (parse error, division by zero, …) come back as `{ ok: false, error }`
 * so the preview can render an inline error chip instead of crashing.
 */
export function evaluateScript(
  src: string,
  data: Record<string, unknown>,
): EvaluateResult {
  let ast: Ast;
  try {
    ast = parseScript(src);
  } catch (err) {
    return { ok: false, value: null, error: (err as Error).message };
  }
  try {
    const value = evalNode(ast, data ?? {});
    return { ok: true, value, error: null };
  } catch (err) {
    return { ok: false, value: null, error: (err as Error).message };
  }
}
